/* eslint-disable jsx-a11y/no-static-element-interactions */
import useUserVarStore from "@app/store/useUserVarStore";
import ReactionToolbar from "@blocks/ReactionToolbar/ReactionToolbar";
import ShareSocialButton from "@blocks/ShareSocial/ShareSocialButton";
import { QnHandGesture } from "@components/Layouts/QuickNews/QnButtons";
import {
  CAROUSEL_TRANSITION_DURATION,
  CAROUSEL_TRANSITION_EASING,
  QN_ANALYTICS_EVENT_BASE,
  QN_SOCIAL_ICON_LIST,
} from "@config/QuickNewsConstants";
import GetReadingTime from "@elements/ArticleParagraph/GetReadingTime";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useArticleReactions from "@hooks/useArticleReactions";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { QNCardType } from "@typings/QuickNews";
import { memo, useCallback, useState } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

import { useCarouselDrag } from "./useCarouselDrag";

export type CarouselProps = {
  item: ProcessedArticleData;
  isActive?: boolean;
  index: number;
  setActiveTab: (tab: QNCardType) => void;
  onPrev: () => void;
  onNext: () => void;
  typeIndex: number;
};

const QnPhotoGalleryCard = memo(function QnPhotoGalleryCard({
  item,
  isActive,
  index,
  setActiveTab,
  onPrev,
  onNext,
  typeIndex,
}: CarouselProps) {
  const { userToken } = useUserVarStore();
  const TmCustomEvent = useTMCustomEventAnalyticsCall();

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: item.id,
    userToken,
    publishDate: item.datetime,
    uniqueSectionName: item.sectionName,
  });

  const readingTime = GetReadingTime(item.articleBody, item.urlPath);
  const images = item.imageOriginalGallery || [];
  const hasImages = images.length > 0;

  const [current, setCurrent] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  /** Toggle caption expansion */
  const toggleExpanded = useCallback(() => {
    setExpandedIndex((prev) => (prev === current ? null : current));
  }, [current]);

  /** Drag/Swipe/Click controls */
  const {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    nextSlide,
    prevSlide,
  } = useCarouselDrag({
    current,
    imagesLength: images.length,
    index,
    itemTitle: item.title,
    setActiveTab,
    onPrev,
    onNext,
    analyticsPrefix: "gallery",
    onSetCurrent: setCurrent,
  });

  /**
   * Background Layers Renderer
   */
  const renderBackgroundLayers = () =>
    images.map((img, i) => (
      <div
        key={`bg-${i}`}
        className="absolute inset-0 transition-opacity duration-800 ease-in-out"
        style={{
          backgroundImage: `url('${img.image?.url}')`,
          backgroundPosition: "50%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#000",
          opacity: i === current ? 0.15 : 0,
          zIndex: i === current ? 10 : 1,
        }}
      />
    ));

  /**
   * Navigation Button Renderer
   */
  const renderNavButtons = () =>
    images.length > 1 &&
    ["left", "right"].map((dir) => (
      <button
        key={`pg-nav-${dir}`}
        onClick={dir === "left" ? prevSlide : nextSlide}
        className={`absolute ${dir}-0 top-0 w-[50px] h-full px-2.5 z-50`}
        aria-label={`Go to ${dir === "left" ? "previous" : "next"} image`}
      />
    ));

  /**
   * Dot Navigation
   */
  const renderDots = () =>
    images.length > 1 && (
      <div className="relative py-2 z-50">
        <div className="w-full flex justify-center gap-2">
          {images.map((_, key) => (
            <button
              key={key}
              onClick={() => {
                setCurrent(key);
              }}
              className={twMerge(
                "w-xxs h-xxs rounded-full bg-white-100 transition-[width] duration-300",
                key === current && "!w-3",
              )}
              aria-label={`Go to image ${key + 1}`}
            />
          ))}
        </div>
      </div>
    );

  return (
    <div className="relative flex flex-col h-full w-full bg-black-100/30 overflow-hidden justify-center m-auto items-start">
      {/* Background Layers */}
      <div className="absolute inset-0">
        {hasImages && renderBackgroundLayers()}
      </div>

      {/* Gallery Wrapper */}
      <div className="relative p-3 w-full h-[445px] mx-auto items-center justify-center z-20">
        <div
          className="gallery-wrapper photo-gallery relative w-full md:w-[430px] mx-auto pb-2.5 overflow-hidden"
          key={`pg-img-${item.id}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {isActive && <QnHandGesture canvasId="qn-tm-picks-hand-gesture" />}

          {/* Slides */}
          {hasImages && (
            <div
              className="flex h-full"
              style={{
                transform: `translateX(-${current * 100}%)`,
                transition: `transform ${CAROUSEL_TRANSITION_DURATION} ${CAROUSEL_TRANSITION_EASING}`,
              }}
            >
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img.image?.url}
                  alt={img.caption}
                  className="w-full flex-shrink-0 px-1"
                />
              ))}
            </div>
          )}

          {/* Prev/Next Buttons */}
          {renderNavButtons()}
        </div>

        {/* Title + Caption */}
        <div className="title py-2.5">
          <h1 className="text-md font-semibold leading-[150%] text-orange-500">
            {item.title}
          </h1>

          {/* Expandable Caption */}
          {images[current] && (
            <button
              onClick={toggleExpanded}
              className={twMerge(
                "cursor-pointer w-full text-left text-red-500 text-2sm leading-[180%] transition-all duration-300 ease-in-out",
                expandedIndex === current ? "line-clamp-none" : "truncate",
              )}
              title={expandedIndex === current ? "Collapse" : "Expand"}
            >
              {images[current].caption}
            </button>
          )}

          {renderDots()}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="absolute z-30 bottom-3 w-full flex items-center justify-between gap-2 px-4">
        <Link
          target="_blank"
          reloadDocument
          to={item.linkTo}
          className="flex h-xl w-[80%] flex-1 justify-center items-center gap-2 rounded-150 bg-white-100 border border-gray-1100 text-red-100 text-center text-3xs font-semibold leading-[120%]"
        >
          Read full article – {readingTime} mins read
        </Link>

        <ReactionToolbar
          articleId={item.id}
          showReactions={showReactions}
          reactions={reactions}
          userReactions={userReactions}
          reactionsError={reactionsError}
          handleReactionClick={handleReactionClick}
          expandStyle="-top-24 -right-12"
          qnStyle
        />

        <ShareSocialButton
          socialIconList={QN_SOCIAL_ICON_LIST}
          pagePath={`/quick-news/${item.id}`}
          mbPopupClassName="absolute right-0"
          quickNews
          quickNewsLight
          onClickGaEvent={() => {
            TmCustomEvent({
              ...QN_ANALYTICS_EVENT_BASE,
              eventAction: `share::${typeIndex + 1}`,
              eventLabel: item.title,
            });
          }}
        />
      </div>
    </div>
  );
});

export default QnPhotoGalleryCard;
